import AppContext from "@/AppContext";
import SortMobile from "@/components/drawer/sort-mobile";
import Layout from "@/components/elements/Layout";
import LoadingContent from "@/components/loader/LoadingContent";
import CarouselTopVideo from "@/components/parts/CarouselTopVideo";
import CarouselVideo from "@/components/parts/CarouselVideo";
import Schema from "@/components/schema/Schema";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetContentByTopicList } from "@/hooks/content";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  getHighlightedContent,
  mostViewedContentByContentTypeName,
  newestContentByContentTypeName,
} from "@/services/api";
import { getSEOByPathname } from "@/services/api/SEO";
import { CONTENT_TYPES } from "@/utils/content";
import { fitIn } from "@/utils/imageHandler";
import { ArrowsDownUp, MagnifyingGlass, Play } from "@phosphor-icons/react";
import Image from "next/legacy/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useContext, useEffect, useState } from "react";

export default function Videos({ seoData }) {
  const appContext = useContext(AppContext);
  const router = useRouter();
  const isMobile = useIsMobile();
  const onFetchError = function (error) {
    console.log("Error", error);
    // toast.error("An error occured when fetching videos");
  };

  const {
    contents,
    isLoading,
    search,
    setSearch,
    topic,
    setTopic,
    sortInput,
    setSortInput,
  } = useGetContentByTopicList({
    type: CONTENT_TYPES.VIDEOS,
    onErrorCallback: onFetchError,
    is_random: false,
    need_pagination: true,
  });

  const [topVideos, setTopVideos] = useState([]);
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [topVideosIds, setTopVideosIds] = useState([]);
  const [newestVideos, setNewestVideos] = useState([]);
  const [newestVideosIds, setNewestVideosIds] = useState([]);
  const [isLoadingNewest, setIsLoadingNewest] = useState(false);
  const [isLoadingTop, setIsLoadingTop] = useState(false);
  const [isLoadingFeatured, setIsLoadingFeatured] = useState(false);

  const getFeaturedContents = async () => {
    setIsLoadingFeatured(true);
    const highlightedReports = await getHighlightedContent({
      contentTypeName: CONTENT_TYPES.VIDEOS,
    });
    setFeaturedVideos(highlightedReports.data.data);
    setIsLoadingFeatured(false);
  };

  const getTopVideos = async () => {
    setIsLoadingTop(true);
    const res = await mostViewedContentByContentTypeName({
      contentTypeName: CONTENT_TYPES.VIDEOS,
    });
    setTopVideos(res.data.data);
    setTopVideosIds(res.data.data.map((i) => i.id));
    setIsLoadingTop(false);
  };

  const getNewestVideos = async () => {
    setIsLoadingNewest(true);
    const res = await newestContentByContentTypeName({
      contentTypeName: CONTENT_TYPES.VIDEOS,
    });
    setNewestVideos(res.data.data);
    setNewestVideosIds(res.data.data.map((i) => i.id));
    setIsLoadingNewest(false);
  };

  useEffect(() => {
    getTopVideos();
    getNewestVideos();
    getFeaturedContents();
  }, []);

  useEffect(() => {
    const { q } = router.query;
    if (q) {
      setSearch(q);
    }
  }, [router.query]);

  const handleSearchInput = (evt) => {
    const searchValue = evt.target.value;
    if (searchValue === "" && sortInput === "") {
      setSortInput("newest");
    }
    setSearch(searchValue);

    // Remove 'q' parameter from URL when search is cleared, only if it exists
    if (searchValue === "" && router.query.q) {
      const { q, ...query } = router.query;
      router.push(
        {
          pathname: router.pathname,
          query: query,
        },
        undefined,
        { shallow: true }
      );
    }
  };

  const structuredData = [
    {
      "@context": "https://schema.org/",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `${process.env.BASE_URL}`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Videos",
          item: `${process.env.BASE_URL}videos`,
        },
      ],
    },
  ];

  const onChange = (e) => {
    setSortInput(e);
  };

  return (
    <div>
      <Schema data={structuredData} />
      <Layout
        seoData={seoData}
        title='Videos'
        description={
          "Watch insightful videos on venture capital and startups. Gain insightful knowledge about VC funding trends in Dubai, UAE, MENA, and Africa's"
        }
      >
        <div className='container relative z-10 py-8 lg:py-16'>
          <h1 className='text-4xl font-semibold leading-tight text-white lg:font-bold lg:text-6xl font-display'>
            Videos
          </h1>
          <p className='mt-3 text-base lg:mt-5 lg:text-lg text-white/50'>
            {seoData.page_description ||
              "Keep up to date with the topics we covered in videos"}
          </p>
          <div className='flex flex-wrap items-center mt-5 gap-x-2 border-white/10 gap-y-3 lg:mt-8'>
            <div className='relative flex-1'>
              <div className='absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none opacity-40'>
                <MagnifyingGlass className='text-lg' />
              </div>
              <Input
                type='text'
                placeholder='Search...'
                value={search}
                onInput={handleSearchInput}
                className='w-full transition-colors text-sm lg:text-base placeholder:text-white/40 !h-10 lg:!h-12 text-white rounded-lg bg-white/10 pl-10 focus:bg-white/15'
              />
            </div>
            {!isMobile ? (
              <Select value={sortInput} onValueChange={onChange}>
                <SelectTrigger
                  hideDropdownIcon
                  className='w-auto text-white border h-9 border-white/5 bg-white/5 hover:bg-white/10'
                >
                  <div className='flex items-center space-x-2'>
                    <ArrowsDownUp className='text-base lg:text-lg' />
                    <SelectValue placeholder={"Most Recent"} />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={"newest"}>Most Recent</SelectItem>
                  <SelectItem value={"most_viewed"}>Most Viewed</SelectItem>
                  {search && <SelectItem value={""}>Most Relevant</SelectItem>}
                </SelectContent>
              </Select>
            ) : (
              <SortMobile
                sortOption={sortInput}
                onChange={onChange}
                options={[
                  { value: "newest", label: "Most Recent" },
                  { value: "most_viewed", label: "Most Viewed" },
                  { value: "", label: "Most Relevant" },
                ]}
              />
            )}
          </div>

          {/* START - HIDE THIS WHEN USER SEARCH */}
          {(isLoadingFeatured || featuredVideos.length > 0) && !search && (
            <>
              {isMobile ? (
                <div className='mt-8'>
                  <CarouselVideo
                    title='Featured Videos'
                    data={featuredVideos.slice(0, 3)}
                    videoHeight={"h-[170px]"}
                    container={false}
                  />
                </div>
              ) : (
                <div className='lg:mt-16'>
                  <div className='flex items-center space-x-5 leading-none text-white'>
                    <span className='text-xl font-medium lg:text-3xl font-display'>
                      Featured Videos
                    </span>
                    <span className='flex-1 h-px bg-gradient-to-r from-yellow-1 to-red-1'></span>
                  </div>
                  {isLoadingFeatured ? (
                    <div className='flex justify-center mt-3 lg:mt-5'>
                      <LoadingContent />
                    </div>
                  ) : (
                    <div className='grid gap-2 mt-3 lg:gap-4 lg:mt-5 lg:grid-cols-3'>
                      {featuredVideos
                        .slice(0, 3)
                        .map((featuredVideo, featuredVideoIndex) => (
                          <Link
                            legacyBehavior
                            key={featuredVideoIndex}
                            href={`/videos/${featuredVideo?.slug}`}
                            passHref
                          >
                            <a className='flex items-start max-w-2xl p-3 space-x-3 rounded-lg lg:p-4 lg:space-x-5 bg-gradient-to-tr from-yellow-1 to-red-1 '>
                              <div className='relative h-16 overflow-hidden rounded-lg lg:h-24 aspect-video'>
                                <Image
                                  src={fitIn(featuredVideo?.thumbnail_url, 400)}
                                  layout='fill'
                                  objectFit='cover'
                                  alt=''
                                  className='absolute inset-0 object-cover w-full h-full'
                                />
                                <span className='absolute inline-flex items-center justify-center -translate-x-1/2 -translate-y-1/2 rounded-full shadow-lg w-9 lg:w-12 h-9 lg:h-12 bg-white/90 backdrop-blur-sm top-1/2 left-1/2'>
                                  <Play
                                    weight='fill'
                                    className='text-base lg:text-xl text-background'
                                  />
                                </span>
                              </div>
                              <div className='flex-1'>
                                <p className='inline-block -mt-1 text-base font-semibold leading-snug tracking-tight text-background lg:leading-tight lg:text-lg font-heading'>
                                  {featuredVideo?.title}
                                </p>
                                <p className='mt-1 text-sm leading-snug text-background/50 lg:mt-2 line-clamp-2'>
                                  {featuredVideo?.meta_desc}
                                </p>
                              </div>
                            </a>
                          </Link>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {!search && (
            <div className='mt-8 lg:mt-12'>
              <CarouselTopVideo
                title='Top 10 Videos'
                data={topVideos}
                videoHeight={"h-[130px] lg:h-[200px]"}
                showMoreLink={""}
                container={false}
              />
              {isLoadingTop && (
                <div className='flex justify-center'>
                  <LoadingContent />
                </div>
              )}
            </div>
          )}
          {!search && (
            <div className='mt-8 lg:mt-12'>
              <CarouselVideo
                title='Recent Releases'
                data={newestVideos}
                videoHeight={"h-[130px] lg:h-[175px]"}
                container={false}
              />
              {isLoadingNewest && (
                <div className='flex justify-center'>
                  <LoadingContent />
                </div>
              )}
            </div>
          )}
          {!search && <hr className='my-5 lg:my-12' />}
          {/* END - HIDE THIS WHEN USER SEARCH */}

          <div className='mt-8 space-y-8 lg:mt-12 lg:space-y-12'>
            {contents.map((topicData, index) => (
              <div key={`video-${index}`}>
                <CarouselVideo
                  title={topicData.name}
                  data={topicData.data}
                  videoHeight={"h-[130px] lg:h-[175px]"}
                  showMoreLink={`/videos/topics/${topicData.slug}`}
                  showMoreText='View All'
                  isLoading={isLoading}
                  container={false}
                />
                {isLoading && (
                  <div className='flex justify-center'>
                    <LoadingContent />
                  </div>
                )}
              </div>
            ))}
          </div>

          {contents.length === 0 && !isLoading && (
            <div className='flex flex-col items-center justify-center py-16'>
              <MagnifyingGlass
                size={48}
                className='mb-4 text-white opacity-50'
              />
              <p className='text-lg text-white text-opacity-80'>
                No videos found
              </p>
              <p className='mt-2 text-white text-opacity-60'>
                Try adjusting your search or filters
              </p>
            </div>
          )}
        </div>
      </Layout>
    </div>
  );
}

export async function getServerSideProps() {
  let seoData = {};
  try {
    const seoResponse = await getSEOByPathname("videos");
    seoData = seoResponse.data;
  } catch (error) {
    console.error("Error fetching SEO data:", error);
  }

  return {
    props: {
      seoData,
    },
  };
}
